import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Receipt, CheckCircle2, Clock, CreditCard } from "lucide-react";

import { fetchAPI, formatMoney, formatShortDate } from "../lib/utils";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const STATUS_META = {
	open: { key: "statusOpen", cls: "bg-volt/10 text-volt border-volt/20" },
	partially_paid: {
		key: "statusPartiallyPaid",
		cls: "bg-acid/10 text-acid border-acid/20",
	},
	overdue: {
		key: "statusOverdue",
		cls: "bg-blaze/10 text-blaze border-blaze/20",
	},
	paid: { key: "statusPaid", cls: "bg-volt/15 text-volt border-volt/30" },
	refunded: { key: "statusRefunded", cls: "bg-muted text-muted-foreground" },
	void: { key: "statusVoid", cls: "bg-muted text-muted-foreground" },
};

function InvoiceCard({ inv }) {
	const { t } = useLanguage();
	const meta = STATUS_META[inv.status] || { key: inv.status, cls: "bg-muted" };
	const outstanding = ["open", "partially_paid", "overdue"].includes(
		inv.status,
	);
	const [paying, setPaying] = useState(false);

	const payOnline = async () => {
		setPaying(true);
		try {
			const res = await fetchAPI(`/my/invoices/${inv.invoice_id}/checkout`, {
				method: "POST",
			});
			if (res?.mode === "external" && res?.redirect_url) {
				window.open(res.redirect_url, "_blank", "noopener,noreferrer");
				toast.info(t("externalPaymentRedirectHint"));
				setPaying(false);
			} else if (res?.redirect_url) {
				window.location.href = res.redirect_url; // hosted Stripe Checkout
			} else {
				setPaying(false);
			}
		} catch (err) {
			toast.error(err.detail || err.message);
			setPaying(false);
		}
	};

	return (
		<Card className="bg-card border-border rounded-sm">
			<CardContent className="p-4 space-y-3">
				<div className="flex items-start justify-between">
					<div>
						<p className="font-mono text-xs text-muted-foreground">
							{inv.number || inv.invoice_id.slice(0, 10)}
						</p>
						<p className="text-sm text-muted-foreground mt-1">
							{inv.due_date
								? `${t("dueDate")}: ${formatShortDate(inv.due_date)}`
								: ""}
						</p>
					</div>
					<Badge className={`rounded-sm border ${meta.cls}`}>
						{t(meta.key)}
					</Badge>
				</div>
				<div className="flex items-end justify-between">
					<span className="label-tactical text-muted-foreground">
						{outstanding ? t("amountDue") : t("total")}
					</span>
					<span
						className={`font-heading text-3xl ${
							inv.status === "overdue" ? "text-blaze" : "text-volt"
						}`}
					>
						{formatMoney(outstanding ? inv.amount_due_cents : inv.total_cents)}
					</span>
				</div>
				{inv.line_items?.length > 0 && (
					<div className="border-t border-border/50 pt-2 space-y-1">
						{inv.line_items.map((li) => (
							<div key={li.line_id} className="flex justify-between text-sm">
								<span className="text-muted-foreground">
									{li.quantity > 1 ? `${li.quantity}× ` : ""}
									{li.description}
								</span>
								<span className="font-mono">
									{formatMoney(li.amount_cents)}
								</span>
							</div>
						))}
					</div>
				)}
				{outstanding && (
					<Button
						className="w-full h-12 text-base bg-volt hover:bg-volt/90 rounded-sm"
						disabled={paying}
						onClick={payOnline}
					>
						<CreditCard className="w-5 h-5 mr-2" />
						{t("payNow")}
					</Button>
				)}
			</CardContent>
		</Card>
	);
}

export function MyPaymentsTab() {
	const { t } = useLanguage();
	const [invoices, setInvoices] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		fetchAPI("/my/invoices")
			.then((data) => setInvoices(data || []))
			.catch((err) => toast.error(err.detail || err.message))
			.finally(() => setLoading(false));
	}, []);

	const { toPay, history } = useMemo(() => {
		const toPay = invoices.filter((i) =>
			["open", "partially_paid", "overdue"].includes(i.status),
		);
		const history = invoices.filter((i) =>
			["paid", "refunded", "void"].includes(i.status),
		);
		return { toPay, history };
	}, [invoices]);

	return (
		<div className="max-w-2xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
			<div>
				<h1 className="font-heading text-3xl flex items-center gap-2">
					<Receipt className="w-7 h-7 text-volt" />
					{t("myPayments")}
				</h1>
				<p className="text-sm text-muted-foreground mt-1">
					{t("athletePaymentsHint")}
				</p>
			</div>

			{loading ? (
				<p className="text-sm text-muted-foreground">{t("loading")}</p>
			) : invoices.length === 0 ? (
				<p className="text-sm text-muted-foreground">{t("noInvoices")}</p>
			) : (
				<>
					<section className="space-y-3">
						<h2 className="label-tactical text-muted-foreground flex items-center gap-2">
							<Clock className="w-4 h-4" />
							{t("toPay")}
						</h2>
						{toPay.length === 0 ? (
							<p className="text-sm text-muted-foreground">—</p>
						) : (
							<div className="space-y-3">
								{toPay.map((inv) => (
									<InvoiceCard key={inv.invoice_id} inv={inv} />
								))}
							</div>
						)}
					</section>

					<section className="space-y-3">
						<h2 className="label-tactical text-muted-foreground flex items-center gap-2">
							<CheckCircle2 className="w-4 h-4" />
							{t("paymentHistory")}
						</h2>
						{history.length === 0 ? (
							<p className="text-sm text-muted-foreground">—</p>
						) : (
							<div className="space-y-3">
								{history.map((inv) => (
									<InvoiceCard key={inv.invoice_id} inv={inv} />
								))}
							</div>
						)}
					</section>
				</>
			)}
		</div>
	);
}
